import { ReactNode, SelectHTMLAttributes, forwardRef } from 'react';

import { ChevronDown } from 'lucide-react';
import clsx from 'clsx';

import { Label } from './label';
import { Fieldset } from './fieldset';

type SelectFieldProps = SelectHTMLAttributes<HTMLSelectElement> & {
  classNameFieldSet?: string;
  label?: string;
  name: string;
  options: Record<string, string>[] | string[]; // { key: label } // [label, label, label]
  defaultLabel?: string;
  enableEmptyOption?: boolean;
  errorMessage?: string;
};

/*
 * Select, a dropdown for an array of options
 * forwardRef is used to pass the ref to the input field, since we are using ReactFormHook
 * @props:
 * extends InputHTMLAttributes<HTMLInputElement>
 * - options: the array of options // { key: label }[]
 * - label: the label of the input field
 * - classNameFieldSet: the class of the fieldset
 * - name: the name of the input field (required)
 */

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>((props, ref) => {
  const {
    id,
    name,
    required,
    options,
    defaultLabel,
    enableEmptyOption,
    classNameFieldSet = ' ',
    className = '',
    label,
    errorMessage,
    ...rest
  } = props;

  function populateOptionElements() {
    const elements: ReactNode[] = [];
    if (enableEmptyOption) {
      elements.push(
        <option key={0} value={''}>
          {defaultLabel ?? '- Select -'}
        </option>
      );
    }
    // transform the data into option elements
    for (const option of options) {
      if (typeof option === 'string') {
        elements.push(
          <option key={`select-option-${name}-${option}`} value={option}>
            {option}
          </option>
        );
        continue;
      }

      const key = Object.keys(option)[0];
      const label = Object.values(option)[0];

      elements.push(
        <option key={`select-option-${name}-${key}`} value={key}>
          {label}
        </option>
      );
    }
    return elements;
  }

  return (
    <Fieldset className={classNameFieldSet}>
      {label && (
        <Label htmlFor={id ?? name} required={required}>
          {label}
        </Label>
      )}
      <div className="relative">
        <select
          {...rest}
          ref={ref}
          className={clsx(
            'w-full appearance-none rounded-md border bg-transparent dark:bg-input/30 px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50',
            'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
            'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
            className
          )}
          id={id ?? name}
          name={name}
          required={required}
        >
          {populateOptionElements()}
        </select>
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <ChevronDown className="h-5 w-5 text-gray-400 sm:h-4 sm:w-4" />
        </div>
      </div>
      {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
    </Fieldset>
  );
});

SelectField.displayName = 'SelectField';
